import {Avatar, ButtonGroup, Choice, TextInput, closeCurrentDialog} from 'platform/components';
import {Box, Center, Clickable, Grid, Heading, HStack, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useGetEmployeeWorkProfessionsListQuery, useGetUsersQuery} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {getActiveOptions} from '@dms/shared';

const LIST_HEIGHT = 80;

interface AddParticipantDialogProps {
  existingEmployeeIds: string[];
  onConfirm: (participant: {employeeId: string; professionId: string}) => void;
}

export function AddParticipantDialog(props: AddParticipantDialogProps) {
  const {data: users, isLoading: areUsersLoading} = useGetUsersQuery();
  const {professionOptions, areProfessionsLoading} = useGetEmployeeWorkProfessionsListQuery(
    undefined,
    {
      selectFromResult: ({data, isLoading}) => ({
        professionOptions: getActiveOptions(data?.professions),
        areProfessionsLoading: isLoading,
      }),
    }
  );

  const [search, setSearch] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [professionId, setProfessionId] = useState<string | null>(null);

  const query = search.trim().toLowerCase();
  const employees = (users ?? [])
    .filter((user) => !props.existingEmployeeIds.includes(user.id))
    .filter((user) => `${user.firstName} ${user.lastName}`.toLowerCase().includes(query));
  const selectedEmployee = users?.find((user) => user.id === selectedEmployeeId) ?? null;

  const handleConfirm = () => {
    if (isNil(selectedEmployeeId) || isNil(professionId)) {
      return;
    }
    props.onConfirm({employeeId: selectedEmployeeId, professionId});
    closeCurrentDialog();
  };

  return (
    <VStack spacing={4}>
      <Text color="secondary">{i18n.t('page.checklistAudits.team.addDialogSubtitle')}</Text>

      <Grid columns={2} spacing={4}>
        <VStack spacing={3}>
          <TextInput
            value={search}
            onChange={(value) => setSearch(value ?? '')}
            placeholder={i18n.t('page.checklistAudits.team.addDialogSearch')}
            data-testid="recordAuditTeam-addDialog-search"
          />
          <Box height={LIST_HEIGHT} overflowY="auto" overflowX="hidden">
            <VStack spacing={1}>
              {employees.map((employee) => {
                const name = `${employee.firstName} ${employee.lastName}`.trim();
                return (
                  <Clickable key={employee.id} onClick={() => setSelectedEmployeeId(employee.id)}>
                    <Box
                      padding={2}
                      borderRadius="small"
                      backgroundColor={
                        employee.id === selectedEmployeeId ? 'palettes.blue.10.100' : undefined
                      }
                    >
                      <HStack spacing={3} align="center">
                        <Avatar name={name} size="small" />
                        <Text size="small" alternative>
                          {name}
                        </Text>
                      </HStack>
                    </Box>
                  </Clickable>
                );
              })}
            </VStack>
          </Box>
        </VStack>

        <Box minHeight={LIST_HEIGHT}>
          {isNotNil(selectedEmployee) ? (
            <VStack spacing={4}>
              <HStack spacing={3} align="center">
                <Avatar
                  name={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`.trim()}
                  size="large"
                />
                <Heading size={4}>
                  {`${selectedEmployee.firstName} ${selectedEmployee.lastName}`.trim()}
                </Heading>
              </HStack>
              <Choice
                label={i18n.t('page.checklistAudits.create.fields.participantRole')}
                value={professionId}
                options={professionOptions}
                isLoading={areProfessionsLoading}
                onChange={(value) => setProfessionId(value)}
                data-testid="recordAuditTeam-addDialog-profession"
              />
            </VStack>
          ) : (
            <Center height="100%">
              <Text color="tertiary">
                {i18n.t('page.checklistAudits.team.addDialogPreviewEmpty')}
              </Text>
            </Center>
          )}
        </Box>
      </Grid>

      <ButtonGroup
        align="right"
        buttons={[
          {
            title: i18n.t('general.actions.cancel'),
            variant: 'secondary',
            onClick: closeCurrentDialog,
          },
          {
            title: i18n.t('page.checklistAudits.create.actions.addParticipant'),
            onClick: handleConfirm,
            isDisabled: isNil(selectedEmployeeId) || isNil(professionId) || areUsersLoading,
          },
        ]}
      />
    </VStack>
  );
}
